import { memo } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { t } from 'i18next';
import { BloxNXTLogo } from 'assets/BloxNXTLogo';
import { NoAccessContent } from 'components/NoAccess/NoAccess';
import { getRoute } from 'utility/functions';

export interface DashboardNoAccessProps {
  header?: string;
  contact?: string;
}

/**
 * DashboardNoAccess Component
 * --------------------------------------------------------
 * Displays a no-access message for dashboards when the user
 * lacks required permissions. On the homepage, it shows a
 * welcome header with the BloxNXT logo. On other dashboard
 * pages, it shows only the NoAccessContent component.
 */
const DashboardNoAccess = memo(({ header, contact }: DashboardNoAccessProps) => {
  const location = useLocation();
  const route = getRoute(location.pathname);
  const isHomepage = route === '/' || route === '';

  const headerMsg = header ?? t('blox.dashboards.user_permissions.title');
  const contactMsg = contact ?? t('blox.dashboards.user_permissions.description');

  // On homepage, render the full welcome experience with logo
  return (
    <Box
      data-testid="dashboard-no-access"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        gap: 3,
      }}
    >
      {/* IF isHomepage render the greeting. */}
      {isHomepage && (
        <Box
          data-testid="dashboard-no-access-header"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: 32,
              mb: 1,
            }}
          >
            {t('blox.welcome.title')}
          </Typography>

          <BloxNXTLogo sx={{ height: 'auto', width: '250px' }} />
        </Box>
      )}

      <NoAccessContent
        actions={false}
        header={headerMsg}
        contact={contactMsg}
      />
    </Box>
  );
});

DashboardNoAccess.displayName = 'DashboardNoAccess';

export default DashboardNoAccess;
